import type {
  TBackofficeNormalizedAddress,
  TGoogleNormalizedAddress,
  TGovernmentNormalizedAddress,
  TNormalizedAddress,
} from "@/domains/address/dto/normalized-address.ts";
import type { BackofficeAddress } from "@/domains/address/providers/backoffice/backoffice-address.ts";
import type { FrenchGovernmentAddress } from "@/domains/address/providers/french-government/french-government-address.ts";
import type { GoogleAddress } from "@/domains/address/providers/google/google-address.ts";
import type { TGeocodedAddressDto } from "@/types/backoffice/dto/site/site-dto.ts";

import { BackofficeAddressToNormalizedAddressAdapter } from "@/domains/address/adapters/backoffice-address-to-normalized-address-adapter.ts";
import { BackofficeNormalizedAddressToBackofficeAddress } from "@/domains/address/adapters/backoffice-normalized-address-to-backoffice-address.ts";
import { FrenchGovernmentAddressToNormalizedAddressAdapter } from "@/domains/address/adapters/french-government-address-to-normalized-address-adapter.ts";
import { FrenchGovernmentNormalizedAddressToBackofficeAddress } from "@/domains/address/adapters/french-government-normalized-address-to-backoffice-address-adapter.ts";
import { GoogleAddressToNormalizeAddressAdapter } from "@/domains/address/adapters/google-address-to-normalized-address-adapter.ts";
import { GoogleNormalizedAddressToBackofficeAddressAdapter } from "@/domains/address/adapters/google-normalized-address-to-backoffice-address-adapter.ts";
import { EAddressProvider } from "@/domains/address/providers/address-provider.types.ts";
import { assert } from "@/lib/utils.ts";

export interface IAddressVisitor<OUTPUT = unknown> {
  visitGovernment: (address: FrenchGovernmentAddress) => OUTPUT;
  visitGoogle: (address: GoogleAddress) => OUTPUT;
  visitBackoffice: (address: BackofficeAddress) => OUTPUT;
}

export type TAddressVisitorOutput<V> = V extends IAddressVisitor<infer R> ? R : never;

export interface INormalizedAddressVisitor<OUTPUT = unknown> {
  visitGovernment: (address: TGovernmentNormalizedAddress) => OUTPUT;
  visitGoogle: (address: TGoogleNormalizedAddress) => OUTPUT;
  visitBackoffice: (address: TBackofficeNormalizedAddress) => OUTPUT;
}

export type TNormalizedAddressVisitorOutput<V> = V extends INormalizedAddressVisitor<infer R> ? R : never;

const normalizedAddressVisitorMap: Record<
  EAddressProvider | "BACKOFFICE",
  <K extends INormalizedAddressVisitor>(visitor: K, address: TNormalizedAddress) => TNormalizedAddressVisitorOutput<K>
> = {
  [EAddressProvider.GOOGLE]: <K extends INormalizedAddressVisitor>(visitor: K, address: TNormalizedAddress) =>
    visitor.visitGoogle(address as TGoogleNormalizedAddress) as TNormalizedAddressVisitorOutput<K>,
  [EAddressProvider.FRENCH_GOVERNMENT]: <K extends INormalizedAddressVisitor>(
    visitor: K,
    address: TNormalizedAddress,
  ) => visitor.visitGovernment(address as TGovernmentNormalizedAddress) as TNormalizedAddressVisitorOutput<K>,
  BACKOFFICE: <K extends INormalizedAddressVisitor>(visitor: K, address: TNormalizedAddress) =>
    visitor.visitBackoffice(address as TBackofficeNormalizedAddress) as TNormalizedAddressVisitorOutput<K>,
};

export const NormalizeAddressVisitor: IAddressVisitor<TNormalizedAddress> = {
  visitGovernment(address: FrenchGovernmentAddress): TGovernmentNormalizedAddress {
    return address.adapt(FrenchGovernmentAddressToNormalizedAddressAdapter);
  },

  visitGoogle(address: GoogleAddress): TGoogleNormalizedAddress {
    return address.adapt(GoogleAddressToNormalizeAddressAdapter);
  },

  visitBackoffice(address: BackofficeAddress): TBackofficeNormalizedAddress {
    return address.adapt(BackofficeAddressToNormalizedAddressAdapter);
  },
};

export function acceptNormalizedAddressVisitor<K extends INormalizedAddressVisitor>(
  address: TNormalizedAddress,
  visitor: K,
): TNormalizedAddressVisitorOutput<K> {
  const visitFn = normalizedAddressVisitorMap[address.provider];
  assert(visitFn !== undefined, `No visitor function defined for address provider: ${address}`);

  return visitFn(visitor, address);
}

export const NormalizedAddressToBackofficeAddressVisitor: INormalizedAddressVisitor<TGeocodedAddressDto> = {
  visitGovernment(address: TGovernmentNormalizedAddress): TGeocodedAddressDto {
    return FrenchGovernmentNormalizedAddressToBackofficeAddress.adapt(address);
  },

  visitGoogle(address: TGoogleNormalizedAddress): TGeocodedAddressDto {
    return GoogleNormalizedAddressToBackofficeAddressAdapter.adapt(address);
  },

  visitBackoffice(address: TBackofficeNormalizedAddress): TGeocodedAddressDto {
    return BackofficeNormalizedAddressToBackofficeAddress.adapt(address);
  },
};
